import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useChangePinStore } from '@lib/store';
import type { GetAllCardsResponse } from '@shared/api';
import { Button, Error, Input, Modal } from '@shared/widgets';

interface IForm {
  currentPin: string;
  newPin: string;
  newPinConfirm: string;
}
const DEFAULT_FORM: IForm = {
  currentPin: '',
  newPin: '',
  newPinConfirm: '',
};

interface IChangeYourPinModalProps {
  card: GetAllCardsResponse;
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
}

export const ChangeYourPinModal = (props: IChangeYourPinModalProps) => {
  const { loading, error, success, change, resetError, clear } = useChangePinStore();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
    reset,
  } = useForm<IForm>({ defaultValues: DEFAULT_FORM });

  useEffect(() => {
    if (loading) return;
    if (error) {
      setError('root', { message: 'Something went wrong. Please try again or contact customer service.' });
      return;
    }
    if (success) {
      clear();
      reset(DEFAULT_FORM);
      props.onClose();
      props.onConfirm?.();
    }
  }, [loading, error, success]);

  const closeHandler = () => {
    reset(DEFAULT_FORM);
    resetError();
    props.onClose();
  };
  const changePinHandler = (data: IForm) => {
    if (data.currentPin === data.newPin) {
      setError('root', { message: 'New pin and current pin must be different.' });
      return;
    }
    if (data.newPin !== data.newPinConfirm) {
      setError('root', { message: 'New pin doesn’t match retyped one.' });
      return;
    }
    change(data, props.card.id);
  };

  return (
    <Modal title='Change Your PIN' isOpen={props.isOpen} onClose={closeHandler}>
      <form onSubmit={handleSubmit(changePinHandler)}>
        <div className='flex flex-col gap-4'>
          <Controller
            name='currentPin'
            control={control}
            rules={{
              required: 'Pin must have 4 digits.',
              minLength: { message: 'Pin must have 4 digits.', value: 4 },
              maxLength: { message: 'Pin must have 4 digits.', value: 4 },
            }}
            render={({ field, fieldState: { error } }) => (
              <div className='flex flex-col gap-2'>
                <Input {...field} label='Current Pin' type='number' maxLength={4} pattern='[0-9]*' onFocus={() => clearErrors('root')} />
                <Error text={error?.message} />
              </div>
            )}
          />
          <Controller
            name='newPin'
            control={control}
            rules={{
              required: 'Pin must have 4 digits.',
              minLength: { message: 'Pin must have 4 digits.', value: 4 },
              maxLength: { message: 'Pin must have 4 digits.', value: 4 },
            }}
            render={({ field, fieldState: { error } }) => (
              <div className='flex flex-col gap-2'>
                <Input {...field} label='New Pin' type='number' maxLength={4} pattern='[0-9]*' onFocus={() => clearErrors('root')} />
                <Error text={error?.message} />
              </div>
            )}
          />
          <Controller
            name='newPinConfirm'
            control={control}
            rules={{
              required: 'Pin must have 4 digits.',
              minLength: { message: 'Pin must have 4 digits.', value: 4 },
              maxLength: { message: 'Pin must have 4 digits.', value: 4 },
            }}
            render={({ field, fieldState: { error } }) => (
              <div className='flex flex-col gap-2'>
                <Input {...field} label='Retype New Pin' type='number' maxLength={4} pattern='[0-9]*' onFocus={() => clearErrors('root')} />
                <Error text={error?.message} />
              </div>
            )}
          />
          <p className='text-[#0f0f11]'>
            Once you submit your request for a new PIN, you will be issued a new card. If you want to change the PIN on your existing card, please
            visit a local ATM.
          </p>
          {errors.root?.message && <Error text={errors.root.message} />}
        </div>
        <div className='mt-8 flex gap-4'>
          <Button fullWidth color='outlined' onClick={closeHandler}>
            Cancel
          </Button>
          <Button fullWidth type='submit' isLoading={loading}>
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
};
